import { keydownHandler } from 'prosemirror-keymap'
import { Command, Plugin, PluginKey, Selection } from 'prosemirror-state'

import { schema } from '@showrunner/prose-schemas'

import { hasTitlePage } from '#choo-app/lib/editor/plugins/titlePageView/helpers'

import { createDecorations } from './decorations'
import { selectAll } from './handlers'
import { TitlePageViewState } from './types'

export const titlePageViewPluginKey = new PluginKey<TitlePageViewState>(
  'titlePageView',
)

/**
 * Plugin that manages the visibility of title page vs main content
 *
 * This plugin:
 * - Tracks whether we're showing the title page or main content
 * - Applies decorations to hide/show content based on view mode
 * - Provides actions to toggle between views
 * - Constrains cursor to visible content area
 */
export const titlePageViewPlugin = new Plugin<TitlePageViewState>({
  key: titlePageViewPluginKey,

  state: {
    init(config, state) {
      return {
        showTitlePageView: false,
        decorations: createDecorations(state, false),
      }
    },

    apply(tr, value, oldState, newState) {
      const meta = tr.getMeta(titlePageViewPluginKey)
      if (meta?.showTitlePageView !== undefined) {
        return {
          showTitlePageView: meta.showTitlePageView,
          decorations: createDecorations(newState, meta.showTitlePageView),
        }
      }

      if (tr.docChanged) {
        return {
          ...value,
          decorations: value.decorations.map(tr.mapping, newState.doc),
        }
      }

      return value
    },
  },

  props: {
    decorations(state) {
      return titlePageViewPluginKey.getState(state)?.decorations
    },
    handleKeyDown: keydownHandler({
      'Mod-a': selectAll,
      ArrowLeft: arrow('left'),
      ArrowRight: arrow('right'),
      ArrowUp: arrow('up'),
      ArrowDown: arrow('down'),
    }),
  },

  view(view) {
    let firstPagePos = 0

    view.state.doc.descendants((node, pos) => {
      if (node.type === schema.nodes.page) {
        firstPagePos = pos
        return false
      }
    })

    const selection = Selection.near(view.state.doc.resolve(firstPagePos))
    view.dispatch(view.state.tr.setSelection(selection))

    return {}
  },
})

function arrow(dir: 'up' | 'down' | 'left' | 'right'): Command {
  return (state, _dispatch, view) => {
    if (!view || !hasTitlePage(state)) return false

    if (!state.selection.empty) return false
    if (!view.endOfTextblock(dir)) return false

    const pluginState = titlePageViewPluginKey.getState(state)
    if (!pluginState) return false

    const side = dir === 'left' || dir === 'up' ? -1 : 1
    const { $from, $to } = state.selection

    const nextNode = side > 0 ? $to.after() : $from.before()
    const nextSelection = Selection.near(state.doc.resolve(nextNode), side)

    const decorations = pluginState.decorations.find(
      nextSelection.$from.pos,
      nextSelection.$to.pos,
    )

    if (decorations.length > 0) return true

    return false
  }
}
